import React from 'react';
import {
  BellIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  UsersIcon,
  PaperAirplaneIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../LoadingSpinner';
import NotificationDashboard from '../notifications/NotificationDashboard';
import { isAdmin } from '../../utils/roles';
import { useAuth } from '../../contexts/AuthContext';

const AdminNotifications = ({
  triggerAvailabilityRemindersMutation,
  triggerScheduleRemindersMutation,
  triggerTestNotificationMutation,
  showNotificationLogs,
  setShowNotificationLogs,
  notificationLogs,
  isLoadingNotificationLogs,
  queryClient,
  trainingGroups = [],
}) => {
  const { user } = useAuth();

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Admin Notifications</h2>
        <p className="mt-1 text-sm text-gray-600">
          Manage notifications and send messages to users
        </p>
      </div>

      <div className="space-y-6">
        {/* Notification Triggers */}
        <div className="rounded-lg border border-gray-200 p-6">
          <h3 className="mb-4 text-lg font-medium text-gray-900">Notification Triggers</h3>
          <div className="space-y-4">
            <div className="flex flex-col items-start justify-between gap-3 rounded-lg border border-gray-200 p-4 sm:flex-row sm:items-center">
              <div>
                <h4 className="text-sm font-medium text-gray-900">Availability Reminders</h4>
                <p className="text-sm text-gray-600">
                  Send reminders to users to fill in their availability
                </p>
              </div>
              <button
                onClick={() => triggerAvailabilityRemindersMutation.mutate(true)}
                disabled={triggerAvailabilityRemindersMutation.isLoading}
                className="flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
              >
                <BellIcon className="h-4 w-4" />
                {triggerAvailabilityRemindersMutation.isLoading ? 'Triggering...' : 'Trigger'}
              </button>
            </div>
            <div className="flex flex-col items-start justify-between gap-3 rounded-lg border border-gray-200 p-4 sm:flex-row sm:items-center">
              <div>
                <h4 className="text-sm font-medium text-gray-900">Test Notification</h4>
                <p className="text-sm text-gray-600">
                  Send a test notification to verify the system is working
                </p>
              </div>
              <button
                onClick={() => triggerTestNotificationMutation.mutate()}
                disabled={triggerTestNotificationMutation.isLoading}
                className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
              >
                <BellIcon className="h-4 w-4" />
                {triggerTestNotificationMutation.isLoading ? 'Sending...' : 'Send Test'}
              </button>
            </div>
            <div className="flex flex-col items-start justify-between gap-3 rounded-lg border border-gray-200 p-4 sm:flex-row sm:items-center">
              <div>
                <h4 className="text-sm font-medium text-gray-900">Schedule Reminders</h4>
                <p className="text-sm text-gray-600">
                  Send reminders about upcoming training sessions
                </p>
              </div>
              <button
                onClick={() => triggerScheduleRemindersMutation.mutate()}
                disabled={triggerScheduleRemindersMutation.isLoading}
                className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
              >
                <BellIcon className="h-4 w-4" />
                {triggerScheduleRemindersMutation.isLoading ? 'Triggering...' : 'Trigger'}
              </button>
            </div>
          </div>
        </div>

        {/* Admin Notification Dashboard - Only show when logs are visible */}
        {<NotificationDashboard />}

        {/* Notification Logs */}
        <div className="rounded-lg border border-gray-200 p-6">
          <div className="mb-4 flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center">
            <h3 className="text-lg font-medium text-gray-900">Notification Logs</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setShowNotificationLogs(!showNotificationLogs);
                  if (!showNotificationLogs) {
                    queryClient.invalidateQueries('notificationLogs');
                  }
                }}
                className="flex items-center gap-2 rounded-md border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {showNotificationLogs ? (
                  <ChevronUpIcon className="h-4 w-4" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4" />
                )}
                {showNotificationLogs ? 'Hide' : 'Show'} Logs
              </button>
            </div>
          </div>

          {showNotificationLogs && (
            <div className="space-y-4">
              {isLoadingNotificationLogs ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : notificationLogs.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
                  <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No notification logs</h3>
                  <p className="mt-1 text-sm text-gray-500">No notifications have been sent yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notificationLogs.map((log) => (
                    <div key={log.id} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <h4 className="break-words text-sm font-medium text-gray-900">
                              {log.title}
                            </h4>
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                log.priority === 'urgent'
                                  ? 'bg-red-100 text-red-800'
                                  : log.priority === 'high'
                                    ? 'bg-orange-100 text-orange-800'
                                    : log.priority === 'normal'
                                      ? 'bg-blue-100 text-blue-800'
                                      : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {log.priority}
                            </span>
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                log.status === 'sent'
                                  ? 'bg-green-100 text-green-800'
                                  : log.status === 'failed'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {log.status}
                            </span>
                          </div>
                          <p className="mb-2 break-words text-sm text-gray-600">{log.message}</p>
                          <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <ClockIcon className="h-3 w-3" />
                              {log.sent_at
                                ? new Date(log.sent_at).toLocaleString()
                                : log.created_at
                                  ? `${new Date(log.created_at).toLocaleString()} (queued)`
                                  : 'Unknown time'}
                            </span>
                            <span className="flex items-center gap-1">
                              <UsersIcon className="h-3 w-3" />
                              {log.recipient_count || 1} recipient
                              {log.recipient_count !== 1 ? 's' : ''}
                            </span>
                            {log.notification_type && (
                              <span className="flex items-center gap-1">
                                <PaperAirplaneIcon className="h-3 w-3" />
                                {log.notification_type}
                              </span>
                            )}
                          </div>
                          {log.action_url && (
                            <div className="mt-2">
                              <span className="text-xs text-gray-500">Action URL: </span>
                              <a
                                href={log.action_url}
                                className="break-words text-xs text-indigo-600 hover:text-indigo-500"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {log.action_url}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AdminNotifications;
